/**
 * exportArtifact — produce real downloadable files (.md / .pdf / .docx) from a
 * multi-agent report.
 *
 * Two entry points:
 *   - downloadMarkdownAs(markdown, format, base): the ALWAYS-AVAILABLE path used
 *     by the answer's Export ▾ control — generates a file straight from the
 *     report markdown on screen, no backend artifact required.
 *   - downloadExportArtifact(id, name): resolves an "Export …" report artifact
 *     (content is JSON `{format, summary, markdown}` or raw markdown) and
 *     delegates to downloadMarkdownAs.
 */
import { getArtifact } from "./api";
import { isWhitespace, isAlphanumeric, isWordChar } from "./strings";

export type ExportFormat = "md" | "pdf" | "docx";

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Identifier immediately before a `.cls`/`.trigger` extension. Leftmost match,
 *  null if none. */
function classTokenBeforeExtension(name: string): string | null {
  for (let i = 0; i < name.length; i++) {
    if (name[i] !== ".") continue;
    const rest = name.slice(i + 1).toLowerCase();
    if (!rest.startsWith("cls") && !rest.startsWith("trigger")) continue;
    let start = i;
    while (start > 0 && isWordChar(name[start - 1])) start--;
    if (start < i) return name.slice(start, i);
  }
  return null;
}

/** Strip a leading, case-insensitive "Export <word/slash>:" prefix. */
function stripExportPrefix(s: string): string {
  if (s.slice(0, 6).toLowerCase() !== "export") return s;
  let i = 6;
  if (i >= s.length || !isWhitespace(s[i])) return s;
  while (i < s.length && isWhitespace(s[i])) i++;
  let j = i;
  while (j < s.length && (isWordChar(s[j]) || s[j] === "/")) j++;
  if (j === i || s[j] !== ":") return s;
  j++;
  while (j < s.length && isWhitespace(s[j])) j++;
  return s.slice(j);
}

/** Replace runs of chars outside [A-Za-z0-9_-] with a single "_". */
function nonFilenameToUnderscore(s: string): string {
  let out = "";
  let inRun = false;
  for (const c of s) {
    if (isAlphanumeric(c) || c === "_" || c === "-") {
      out += c;
      inRun = false;
    } else if (!inRun) {
      out += "_";
      inRun = true;
    }
  }
  return out;
}

function trimChar(s: string, ch: string): string {
  let a = 0;
  let b = s.length;
  while (a < b && s[a] === ch) a++;
  while (b > a && s[b - 1] === ch) b--;
  return s.slice(a, b);
}

/** Derive a safe, human filename base. */
function cleanBase(name: string): string {
  const cls = classTokenBeforeExtension(name);
  if (cls) return cls;
  const stripped = trimChar(nonFilenameToUnderscore(stripExportPrefix(name)), "_");
  return stripped.slice(0, 60) || "report";
}

/** Remove `![alt](url)` images entirely. */
function removeImages(s: string): string {
  let out = "";
  let i = 0;
  while (i < s.length) {
    if (s[i] === "!" && s[i + 1] === "[") {
      let j = i + 2;
      while (j < s.length && s[j] !== "]") j++;
      if (j < s.length && s[j + 1] === "(") {
        let k = j + 2;
        while (k < s.length && s[k] !== ")") k++;
        if (k < s.length) {
          i = k + 1;
          continue;
        }
      }
    }
    out += s[i];
    i++;
  }
  return out;
}

/** `[text](url)` → `text` (text is 1+ non-`]` chars). */
function linksToText(s: string): string {
  let out = "";
  let i = 0;
  while (i < s.length) {
    if (s[i] === "[") {
      let j = i + 1;
      while (j < s.length && s[j] !== "]") j++;
      if (j < s.length && j > i + 1 && s[j + 1] === "(") {
        let k = j + 2;
        while (k < s.length && s[k] !== ")") k++;
        if (k < s.length) {
          out += s.slice(i + 1, j);
          i = k + 1;
          continue;
        }
      }
    }
    out += s[i];
    i++;
  }
  return out;
}

/** Unwrap a single-char paired delimiter: `` `code` `` / `*italic*` (inner 1+). */
function unwrapPaired(s: string, delim: string): string {
  let out = "";
  let i = 0;
  while (i < s.length) {
    if (s[i] === delim) {
      let j = i + 1;
      while (j < s.length && s[j] !== delim) j++;
      if (j < s.length && j > i + 1) {
        out += s.slice(i + 1, j);
        i = j + 1;
        continue;
      }
    }
    out += s[i];
    i++;
  }
  return out;
}

/** Unwrap a double-char paired delimiter: `**bold**` / `__bold__` (inner 1+, no delim char). */
function unwrapPairedDouble(s: string, delim: string): string {
  let out = "";
  let i = 0;
  while (i < s.length) {
    if (s[i] === delim && s[i + 1] === delim) {
      let j = i + 2;
      while (j < s.length && s[j] !== delim) j++;
      if (j + 1 < s.length && s[j] === delim && s[j + 1] === delim && j > i + 2) {
        out += s.slice(i + 2, j);
        i = j + 2;
        continue;
      }
    }
    out += s[i];
    i++;
  }
  return out;
}

/** Strip inline markdown markers for clean rendered text (bold/italic/code/links). */
function stripInline(s: string): string {
  let r = removeImages(s);
  r = linksToText(r);
  r = unwrapPaired(r, "`");
  r = unwrapPairedDouble(r, "*");
  r = unwrapPaired(r, "*");
  r = unwrapPairedDouble(r, "_");
  return r.trim();
}

type Block =
  | { kind: "h1" | "h2" | "h3" | "p"; text: string }
  | { kind: "bullet" | "ordered"; text: string }
  | { kind: "table" | "code"; text: string }
  | { kind: "mermaid"; text: string; index: number }
  | { kind: "blank" };

/** A line that, after leading whitespace, starts with a ``` fence. */
function startsWithFence(line: string): boolean {
  let i = 0;
  while (i < line.length && isWhitespace(line[i])) i++;
  return line.slice(i, i + 3) === "```";
}

/** Language tag right after an opening ``` fence, lower-cased ("" if none).
 *  e.g. "```mermaid" -> "mermaid". */
function fenceLang(line: string): string {
  let i = 0;
  while (i < line.length && isWhitespace(line[i])) i++;
  i += 3; // past the ```
  let j = i;
  while (j < line.length && isWordChar(line[j])) j++;
  return line.slice(i, j).toLowerCase();
}

/** Parse a heading line (1+ leading "#" followed by whitespace). */
function parseHeading(line: string): { level: "h1" | "h2" | "h3"; text: string } | null {
  let h = 0;
  while (h < line.length && line[h] === "#") h++;
  if (h === 0 || h >= line.length || !isWhitespace(line[h])) return null;
  let i = h;
  while (i < line.length && isWhitespace(line[i])) i++;
  const level = h === 1 ? "h1" : h === 2 ? "h2" : "h3";
  return { level, text: line.slice(i) };
}

/** A markdown-table separator row — `t` is already trimmed. */
function isTableSeparator(t: string): boolean {
  if (t.length < 3) return false;
  const inner = t.slice(1, t.length - 1);
  if (inner.length < 1) return false;
  for (const c of inner) {
    if (!(isWhitespace(c) || c === ":" || c === "|" || c === "-")) return false;
  }
  return true;
}

/** Parse a bullet line (leading whitespace, a dash/star/plus marker, then whitespace). */
function parseBullet(line: string): string | null {
  let i = 0;
  while (i < line.length && isWhitespace(line[i])) i++;
  const c = line[i];
  if (c !== "-" && c !== "*" && c !== "+") return null;
  i++;
  if (i >= line.length || !isWhitespace(line[i])) return null;
  while (i < line.length && isWhitespace(line[i])) i++;
  return line.slice(i);
}

/** Parse an ordered-list line (leading whitespace, digits, ".", whitespace). */
function parseOrdered(line: string): string | null {
  let i = 0;
  while (i < line.length && isWhitespace(line[i])) i++;
  let d = i;
  while (d < line.length && line[d] >= "0" && line[d] <= "9") d++;
  if (d === i || line[d] !== ".") return null;
  let j = d + 1;
  if (j >= line.length || !isWhitespace(line[j])) return null;
  while (j < line.length && isWhitespace(line[j])) j++;
  return line.slice(j);
}

/** Lightweight markdown → block list (headings, bullets, tables, code, paras). */
function parseBlocks(md: string): Block[] {
  const out: Block[] = [];
  let inCode = false;
  let inMermaid = false;        // inside a ```mermaid fence
  let mermaidBuf: string[] = [];
  let mermaidCount = 0;
  for (const raw of md.split("\n")) {
    const line = raw.endsWith("\r") ? raw.slice(0, -1) : raw;
    if (startsWithFence(line)) {
      if (inMermaid) {
        // closing a mermaid fence → emit ONE mermaid block (image slot index).
        out.push({ kind: "mermaid", text: mermaidBuf.join("\n"), index: mermaidCount });
        mermaidCount += 1;
        mermaidBuf = [];
        inMermaid = false;
        continue;
      }
      if (!inCode && fenceLang(line) === "mermaid") { inMermaid = true; continue; }
      inCode = !inCode;
      continue;
    }
    if (inMermaid) { mermaidBuf.push(line); continue; }
    if (inCode) { out.push({ kind: "code", text: line }); continue; }
    if (!line.trim()) { out.push({ kind: "blank" }); continue; }
    const heading = parseHeading(line);
    if (heading) { out.push({ kind: heading.level, text: stripInline(heading.text) }); continue; }
    const t = line.trim();
    if (t.startsWith("|") && t.endsWith("|") && t.length >= 2) {
      if (!isTableSeparator(t)) out.push({ kind: "table", text: t }); // skip |---| separators
      continue;
    }
    const bullet = parseBullet(line);
    if (bullet !== null) { out.push({ kind: "bullet", text: stripInline(bullet) }); continue; }
    const ordered = parseOrdered(line);
    if (ordered !== null) { out.push({ kind: "ordered", text: stripInline(ordered) }); continue; }
    out.push({ kind: "p", text: stripInline(line) });
  }
  return out;
}

/** Collapse markdown pipes (with surrounding whitespace) into 3-space gaps, then
 *  trim. */
function formatTableRowForPdf(s: string): string {
  let out = "";
  let i = 0;
  while (i < s.length) {
    let j = i;
    while (j < s.length && isWhitespace(s[j])) j++;
    if (j < s.length && s[j] === "|") {
      j++;
      while (j < s.length && isWhitespace(s[j])) j++;
      out += "   ";
      i = j;
      continue;
    }
    out += s[i];
    i++;
  }
  return out.trim();
}

/** Escape plain text for safe HTML insertion (used for the title; the body is
 *  rendered/escaped by react-markdown). No regex. */
function escapeHtmlText(s: string): string {
  let out = "";
  for (const c of s) {
    out += c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : c;
  }
  return out;
}

/** Swap each ```mermaid fence for the rendered diagram image (in document order) so
 *  the print render shows the diagram, not the source. Plain scan, no regex. */
function replaceMermaidWithImages(md: string, diagrams: ReportDiagram[]): string {
  const out: string[] = [];
  let inMermaid = false;
  let buf: string[] = [];
  let idx = 0;
  for (const raw of md.split("\n")) {
    const line = raw.endsWith("\r") ? raw.slice(0, -1) : raw;
    if (startsWithFence(line)) {
      if (inMermaid) {
        const d = diagrams[idx];
        if (d?.dataUrl) out.push("", `![Diagram](${d.dataUrl})`, "");
        else out.push("```", ...buf, "```");
        idx += 1; buf = []; inMermaid = false; continue;
      }
      if (fenceLang(line) === "mermaid") { inMermaid = true; buf = []; continue; }
      out.push(line); continue;
    }
    if (inMermaid) { buf.push(line); continue; }
    out.push(line);
  }
  if (inMermaid) out.push("```mermaid", ...buf, "```");
  return out.join("\n");
}

/** Render report markdown to HTML with the SAME engine the UI uses (react-markdown +
 *  remark-gfm) so code blocks, tables, and headings survive the export. Lazy imports
 *  keep these out of the main bundle. */
async function renderMarkdownToHtml(md: string): Promise<string> {
  const [{ renderToStaticMarkup }, { createElement }, rm, rg] = await Promise.all([
    import("react-dom/server"),
    import("react"),
    import("react-markdown"),
    import("remark-gfm"),
  ]);
  return renderToStaticMarkup(createElement(rm.default, { remarkPlugins: [rg.default] }, md));
}

/** Document stylesheet for the printed report — dark IDE-style code blocks, real
 *  table grids, clean typography. */
const PRINT_CSS = `
*{box-sizing:border-box}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#1a1a1a;line-height:1.6;margin:0;padding:32px 40px;font-size:13px}
.report{max-width:820px;margin:0 auto}
.report-title{font-size:24px;font-weight:700;margin:0 0 18px;padding-bottom:10px;border-bottom:2px solid #e5e7eb}
h1{font-size:20px}h2{font-size:16px;border-bottom:1px solid #eee;padding-bottom:4px}h3{font-size:14px}
h1,h2,h3,h4{font-weight:700;margin:20px 0 8px;line-height:1.3}
p{margin:8px 0}ul,ol{margin:8px 0;padding-left:22px}li{margin:3px 0}
a{color:#2563eb;text-decoration:none}
code{font-family:'SF Mono',Menlo,Consolas,monospace;font-size:12px;background:#f3f4f6;padding:1.5px 5px;border-radius:4px;color:#b91c1c}
pre{background:#0f172a;color:#e2e8f0;padding:14px 16px;border-radius:8px;overflow-x:auto;margin:12px 0;font-size:12px;line-height:1.5}
pre code{background:none;padding:0;color:inherit}
table{border-collapse:collapse;width:100%;margin:12px 0;font-size:12px}
th,td{border:1px solid #d1d5db;padding:6px 10px;text-align:left;vertical-align:top}
th{background:#f3f4f6;font-weight:600}
tr:nth-child(even) td{background:#fafafa}
blockquote{border-left:3px solid #d1d5db;margin:12px 0;padding:2px 14px;color:#555;background:#f9fafb}
img{max-width:100%;height:auto;margin:12px 0}
hr{border:none;border-top:1px solid #e5e7eb;margin:18px 0}
@media print{body{padding:0}.report{max-width:none}pre,table,blockquote,img{break-inside:avoid}}
`;

/**
 * Write a holding page into a just-opened print window.
 *
 * The window is opened synchronously inside the click (to keep user-activation), but
 * the report body is now assembled asynchronously — the cited artifacts have to be
 * fetched for the appendix. A popup left completely unwritten for that long reads as
 * a broken tab, and some browsers reclaim it. This is overwritten by the real
 * document via `document.open()` when the body is ready.
 */
export function writePrintPlaceholder(win: Window | null | undefined, title?: string): void {
  if (!win) return;
  try {
    win.document.open();
    win.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtmlText(title || "Report")}</title>` +
        `<style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;` +
        `color:#374151;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-size:14px}</style>` +
        `</head><body>Preparing report — collecting cited evidence…</body></html>`,
    );
    win.document.close();
  } catch {
    /* a popup we cannot write to is handled by openPrintWindow's own fallback */
  }
}

/** Open the styled report in a new window and trigger Save-as-PDF. Returns false if
 *  the popup was blocked (caller falls back to jsPDF). */
function openPrintWindow(bodyHtml: string, title: string, preWin?: Window | null): boolean {
  // Prefer a window the caller opened SYNCHRONOUSLY inside the click handler —
  // opening here (after awaits) loses the user-activation and the browser
  // backgrounds the popup, which makes window.print() emit only the first page.
  const w = preWin ?? window.open("", "_blank");
  if (!w) return false;
  const heading = title ? `<h1 class="report-title">${escapeHtmlText(title)}</h1>` : "";
  const docHtml =
    `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtmlText(title || "Report")}</title>` +
    `<style>${PRINT_CSS}</style></head><body><main class="report">${heading}${bodyHtml}</main>` +
    `<script>window.addEventListener('load',function(){setTimeout(function(){window.focus();window.print();},300);});</script>` +
    `</body></html>`;
  w.document.open();
  w.document.write(docHtml);
  w.document.close();
  return true;
}

/**
 * Generate + download a file from report markdown. No backend artifact needed —
 * this is what the answer's Export ▾ control calls with the on-screen report.
 * PDF + DOCX render the markdown STRUCTURE (headings, bullets, tables), not a
 * raw monospace dump.
 */
export async function downloadMarkdownAs(
  markdown: string,
  format: ExportFormat,
  base: string,
  opts?: { diagrams?: ReportDiagram[]; title?: string; printWin?: Window | null },
): Promise<void> {
  const safeBase = cleanBase(base);
  const text = (typeof markdown === "string" ? markdown : String(markdown ?? "")).trim() || "(empty report)";
  const diagrams = opts?.diagrams || [];
  const title = (opts?.title || "").trim();

  if (format === "md") {
    // .md keeps the mermaid source verbatim — it renders wherever mermaid is supported.
    const head = title ? `# ${title}\n\n` : "";
    triggerDownload(new Blob([head + text], { type: "text/markdown;charset=utf-8" }), `${safeBase}.md`);
    return;
  }

  const blocks = parseBlocks(text);

  if (format === "pdf") {
    // Rich path: render with the UI's markdown engine → styled print → Save-as-PDF
    // (real code blocks, table grids, headings). Falls back to jsPDF if the popup is
    // blocked or the HTML render throws.
    try {
      const bodyHtml = await renderMarkdownToHtml(replaceMermaidWithImages(text, diagrams));
      if (openPrintWindow(bodyHtml, title, opts?.printWin)) return;
    } catch {
      /* fall through to the jsPDF block render */
    }
    // The pre-opened print window couldn't be used (render threw / blocked) —
    // close it so it doesn't linger blank before the jsPDF fallback downloads.
    try { opts?.printWin?.close(); } catch { /* ignore */ }
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const margin = 44;
    const maxWidth = doc.internal.pageSize.getWidth() - margin * 2;
    const pageBottom = doc.internal.pageSize.getHeight() - margin;
    let y = margin;
    const write = (
      str: string,
      o: { size?: number; bold?: boolean; mono?: boolean; indent?: number; gap?: number },
    ) => {
      const { size = 10, bold = false, mono = false, indent = 0, gap = 14 } = o;
      doc.setFont(mono ? "courier" : "helvetica", bold ? "bold" : "normal");
      doc.setFontSize(size);
      const wrapped = doc.splitTextToSize(str || " ", maxWidth - indent) as string[];
      for (const w of wrapped) {
        if (y > pageBottom) { doc.addPage(); y = margin; }
        doc.text(w, margin + indent, y);
        y += gap;
      }
    };
    if (title) { write(title, { size: 19, bold: true, gap: 26 }); y += 4; }
    for (const b of blocks) {
      switch (b.kind) {
        case "blank": y += 6; break;
        case "h1": y += 4; write(b.text, { size: 17, bold: true, gap: 21 }); break;
        case "h2": y += 3; write(b.text, { size: 13, bold: true, gap: 17 }); break;
        case "h3": write(b.text, { size: 11, bold: true, gap: 15 }); break;
        case "bullet": write(`•  ${b.text}`, { indent: 12 }); break;
        case "ordered": write(b.text, { indent: 12 }); break;
        case "table": write(formatTableRowForPdf(b.text), { size: 8.5, mono: true, gap: 12 }); break;
        case "code": write(b.text, { size: 8.5, mono: true, gap: 12 }); break;
        case "mermaid": {
          const d = diagrams[b.index];
          if (d?.dataUrl && d.w > 0) {
            const drawW = Math.min(maxWidth, d.w);
            const drawH = (d.h * drawW) / d.w;
            if (y + drawH > pageBottom) { doc.addPage(); y = margin; }
            try { doc.addImage(d.dataUrl, "PNG", margin, y, drawW, drawH); y += drawH + 12; }
            catch { write("Architecture diagram (source):", { size: 9, bold: true });
                    for (const ln of b.text.split("\n")) write(ln, { size: 8.5, mono: true, gap: 12 }); }
          } else {
            write("Architecture diagram (source):", { size: 9, bold: true });
            for (const ln of b.text.split("\n")) write(ln, { size: 8.5, mono: true, gap: 12 });
          }
          break;
        }
        default: write(b.text, {});
      }
    }
    doc.save(`${safeBase}.pdf`);
    return;
  }

  // docx
  const { Document, Packer, Paragraph, TextRun, HeadingLevel, ImageRun } = await import("docx");
  const paras: InstanceType<typeof Paragraph>[] = [];
  if (title) paras.push(new Paragraph({ text: title, heading: HeadingLevel.TITLE }));
  for (const b of blocks) {
    switch (b.kind) {
      case "blank": paras.push(new Paragraph({ children: [] })); break;
      case "h1": paras.push(new Paragraph({ text: b.text, heading: HeadingLevel.HEADING_1 })); break;
      case "h2": paras.push(new Paragraph({ text: b.text, heading: HeadingLevel.HEADING_2 })); break;
      case "h3": paras.push(new Paragraph({ text: b.text, heading: HeadingLevel.HEADING_3 })); break;
      case "bullet":
      case "ordered": paras.push(new Paragraph({ text: b.text, bullet: { level: 0 } })); break;
      case "table":
      case "code":
        paras.push(new Paragraph({ children: [new TextRun({ text: b.text, font: "Consolas", size: 18 })] }));
        break;
      case "mermaid": {
        const d = diagrams[b.index];
        let placed = false;
        if (d?.dataUrl && d.w > 0) {
          try {
            const bytes = dataUrlToUint8(d.dataUrl);
            const width = Math.min(600, Math.round(d.w));
            const height = Math.round((d.h * width) / d.w);
            paras.push(new Paragraph({
              children: [new ImageRun({ type: "png", data: bytes, transformation: { width, height } })],
            }));
            placed = true;
          } catch { placed = false; }
        }
        if (!placed) {
          paras.push(new Paragraph({ children: [new TextRun({ text: b.text, font: "Consolas", size: 18 })] }));
        }
        break;
      }
      default:
        paras.push(new Paragraph({ children: [new TextRun({ text: b.text, font: "Calibri", size: 22 })] }));
    }
  }
  const doc = new Document({ sections: [{ children: paras }] });
  const blob = await Packer.toBlob(doc);
  triggerDownload(blob, `${safeBase}.docx`);
}

export type ReportDiagram = { dataUrl: string; w: number; h: number };

/** Decode a `data:image/png;base64,...` URL to bytes for docx ImageRun. */
function dataUrlToUint8(dataUrl: string): Uint8Array {
  const comma = dataUrl.indexOf(",");
  const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
}

/**
 * Rasterize the mermaid diagrams ALREADY rendered in the answer DOM (by
 * @streamdown/mermaid) to PNGs, in document order. Best-effort: a diagram that
 * can't be rasterized (e.g. tainted canvas) is returned with an empty dataUrl so
 * the exporter falls back to the mermaid source. `container` scopes the search to
 * the answer so side-panel diagrams aren't picked up.
 */
export async function collectRenderedDiagrams(container: HTMLElement | null): Promise<ReportDiagram[]> {
  if (!container) return [];
  const svgs = Array.from(container.querySelectorAll("svg")).filter((s) => {
    const id = (s.getAttribute("id") || "").toLowerCase();
    return id.includes("mermaid") || s.getAttribute("aria-roledescription") !== null
      || (s.querySelector("g.nodes, g.edgePaths") !== null);
  }) as SVGSVGElement[];
  const out: ReportDiagram[] = [];
  for (const svg of svgs) {
    out.push(await svgToPng(svg));
  }
  return out;
}

async function svgToPng(svg: SVGSVGElement): Promise<ReportDiagram> {
  try {
    const rect = svg.getBoundingClientRect();
    const vb = svg.viewBox?.baseVal;
    const w = Math.round(rect.width || vb?.width || 800);
    const h = Math.round(rect.height || vb?.height || 600);
    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.setAttribute("width", String(w));
    clone.setAttribute("height", String(h));
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const xml = new XMLSerializer().serializeToString(clone);
    const url = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = () => rej(new Error("svg img")); img.src = url; });
    const scale = 2;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, w * scale);
    canvas.height = Math.max(1, h * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) return { dataUrl: "", w: 0, h: 0 };
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(scale, scale);
    ctx.drawImage(img, 0, 0, w, h);
    return { dataUrl: canvas.toDataURL("image/png"), w, h };
  } catch {
    return { dataUrl: "", w: 0, h: 0 };  // exporter falls back to mermaid source
  }
}

/**
 * Resolve an export artifact and download it in its declared format.
 * `displayName` is the link/artifact label (filename + fallback format hint).
 */
export async function downloadExportArtifact(
  artifactId: string,
  displayName: string,
): Promise<void> {
  const art = await getArtifact(artifactId);
  let markdown = art.content || "";
  let format = "";
  try {
    const j = JSON.parse(art.content || "{}") as {
      markdown?: unknown;
      content?: unknown;
      format?: unknown;
    };
    if (typeof j.markdown === "string") markdown = j.markdown;
    else if (typeof j.content === "string") markdown = j.content;
    if (typeof j.format === "string") format = j.format;
  } catch {
    /* content wasn't JSON — use the raw text as markdown */
  }
  if (!format) {
    const lowerName = displayName.toLowerCase();
    if (lowerName.includes("pdf")) format = "pdf";
    else if (lowerName.includes("docx") || lowerName.includes("word")) format = "docx";
    else format = "md";
  }
  const lowerFormat = format.toLowerCase();
  const fmt: ExportFormat =
    lowerFormat.includes("pdf")
      ? "pdf"
      : lowerFormat.includes("docx") || lowerFormat.includes("word")
        ? "docx"
        : "md";
  await downloadMarkdownAs(markdown, fmt, displayName);
}
